// Sets API-helpers.
//
// Wraps backend-endpoints i `/api/sets/*` och `/api/workspaces/:wsId/sets`.
// Alla calls returnerar normaliserad data — payload-formen från backend är
// `{ data: { ... } }` så vi unwrappar i varje helper.

import { api } from './client';
import type {
  SetSummary,
  SetImage,
  CreateSetPayload,
  UpdateSetPayload,
  ListSetsFilters,
  ListSetImagesFilters,
  BulkAddImagesResult,
} from './setsTypes';

interface DataEnvelope<T> {
  data: T;
}

// ─── List sets per workspace ─────────────────────────────────────────

interface ListSetsResponse {
  total: number;
  limit: number;
  offset: number;
  sets: SetSummary[];
}

export async function listSets(
  workspaceId: string,
  filters?: ListSetsFilters,
): Promise<SetSummary[]> {
  const data = await api.get<DataEnvelope<ListSetsResponse>>(
    `/workspaces/${workspaceId}/sets`,
    {
      query: {
        tag: filters?.tag,
        search: filters?.search,
        sort: filters?.sort,
        limit: filters?.limit,
        offset: filters?.offset,
      },
    },
  );
  return data.data.sets;
}

// ─── Get single set ──────────────────────────────────────────────────

interface GetSetResponse {
  set: SetSummary;
}

export async function getSet(id: string): Promise<SetSummary> {
  const data = await api.get<DataEnvelope<GetSetResponse>>(`/sets/${id}`);
  return data.data.set;
}

// ─── Create set ──────────────────────────────────────────────────────

export async function createSet(payload: CreateSetPayload): Promise<SetSummary> {
  const { workspaceId, ...body } = payload;
  // Backend lägger setet i workspace via path-param, övrigt i body.
  const data = await api.post<DataEnvelope<GetSetResponse>>(
    `/workspaces/${workspaceId}/sets`,
    body,
  );
  return data.data.set;
}

// ─── Update set ──────────────────────────────────────────────────────

export async function updateSet(
  id: string,
  patch: UpdateSetPayload,
): Promise<SetSummary> {
  const data = await api.patch<DataEnvelope<GetSetResponse>>(
    `/sets/${id}`,
    patch,
  );
  return data.data.set;
}

// ─── Delete set ──────────────────────────────────────────────────────

export async function deleteSet(id: string): Promise<void> {
  await api.delete(`/sets/${id}`);
}

// ─── Set images: list ────────────────────────────────────────────────

interface SetImagesResponse {
  total: number;
  limit: number;
  offset: number;
  images: SetImage[];
}

export async function getSetImages(
  setId: string,
  filters?: ListSetImagesFilters,
): Promise<SetImage[]> {
  // Vi pagerar genom alla bilder — sets förväntas vara hanterbara i storlek
  // (sällan över ~500), men följer samma pattern som listImages för säkerhets skull.
  const PAGE_SIZE = filters?.limit ?? 500;
  const all: SetImage[] = [];
  let offset = filters?.offset ?? 0;

  while (true) {
    const res = await api.get<DataEnvelope<SetImagesResponse>>(
      `/sets/${setId}/images`,
      {
        query: {
          sort: filters?.sort,
          limit: PAGE_SIZE,
          offset,
        },
      },
    );
    const page = res.data.images;
    all.push(...page);
    if (page.length < PAGE_SIZE) break;
    if (all.length >= res.data.total) break;
    offset += PAGE_SIZE;
    if (offset > 10000) break;
  }
  return all;
}

// ─── Set images: bulk add ────────────────────────────────────────────

export async function addImagesToSet(
  setId: string,
  imageIds: string[],
): Promise<BulkAddImagesResult> {
  const res = await api.post<DataEnvelope<BulkAddImagesResult>>(
    `/sets/${setId}/images`,
    { imageIds },
  );
  return res.data;
}

// ─── Set images: remove single ───────────────────────────────────────

export async function removeImageFromSet(
  setId: string,
  imageId: string,
): Promise<void> {
  await api.delete(`/sets/${setId}/images/${imageId}`);
}

// ─── Set images: bulk reorder ─────────────────────────────────────────

/**
 * Sätter ny ordning på alla bilder i ett set. Backend tilldelar sort_order
 * 100, 200, 300 ... så vi kan klämma in nya bilder mellan senare.
 */
export async function reorderSetImages(
  setId: string,
  imageIds: string[],
): Promise<{ count: number }> {
  const res = await api.patch<DataEnvelope<{ ok: boolean; count: number }>>(
    `/sets/${setId}/images/order`,
    { imageIds },
  );
  return { count: res.data.count };
}

// ─── Set tags ────────────────────────────────────────────────────────

export async function addTagToSet(setId: string, tag: string): Promise<string> {
  const res = await api.post<DataEnvelope<{ tag: string }>>(
    `/sets/${setId}/tags`,
    { tag },
  );
  return res.data.tag;
}

export async function removeTagFromSet(
  setId: string,
  tag: string,
): Promise<void> {
  // Tag-param normaliseras på server-sidan; encoda för säkerhets skull.
  await api.delete(`/sets/${setId}/tags/${encodeURIComponent(tag)}`);
}

// ─── Image → sets-lookup (för Detail-view-chips) ─────────────────────
//
// Returnerar minimala set-objekt (id, name, slug, color) för alla sets
// bilden tillhör inom user's workspaces.

export interface ImageSetMembership {
  id: string;
  name: string;
  slug: string;
  color: string | null;
  workspaceId: string;
  addedAt: string;
  addedBy: number;
}

export async function getImageSets(imageId: string): Promise<ImageSetMembership[]> {
  const res = await api.get<DataEnvelope<{ sets: ImageSetMembership[] }>>(
    `/images/${imageId}/sets`,
  );
  return res.data.sets;
}

// ─── ZIP request ─────────────────────────────────────────────────────

interface SetZipRequestResponse {
  imageIds: string[];
  filename: string;
}

export async function getSetZipRequest(
  setId: string,
): Promise<SetZipRequestResponse> {
  const res = await api.post<DataEnvelope<SetZipRequestResponse>>(
    `/sets/${setId}/zip-request`,
  );
  return res.data;
}
